import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ManhwaCard from '@/components/feature/ManhwaCard';
import useSeo from '@/hooks/useSeo';
import { categories, manhwaList } from '@/mocks/manhwa';

const catIcons: Record<string, string> = {
  urban: 'ri-building-2-line',
  fantasy: 'ri-sparkling-2-line',
  romance: 'ri-heart-3-line',
  action: 'ri-sword-line',
  thriller: 'ri-ghost-line',
  rebirth: 'ri-time-line',
  healing: 'ri-sun-line',
};

export default function CategoryPage() {
  const { id } = useParams<{ id: string }>();
  const cat = categories.find((c) => c.id === id);
  const works = cat ? manhwaList.filter((m) => m.category === cat.id) : [];
  const top = [...works].sort((a, b) => b.score - a.score)[0];

  useSeo({
    title: cat
      ? `${cat.name}韩漫推荐 | ${cat.name}书单精选必看 - 好看的韩漫推荐`
      : '页面不存在 - 好看的韩漫推荐',
    description: cat
      ? `好看的${cat.name}韩漫推荐：${cat.tagline}，为你整理${cat.name}题材下高分必看与冷门宝藏作品，共 ${works.length} 部，追更不迷路。`
      : '您访问的页面不存在。',
    keywords: cat ? `韩漫推荐,${cat.name}韩漫,${cat.name}漫画推荐,韩漫书单,${cat.name}书单` : '韩漫推荐',
    path: `/category/${id ?? ''}`,
    type: 'website',
    jsonLd: cat
      ? [{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${cat.name}韩漫推荐书单`,
          description: cat.desc,
          breadcrumb: { '@type': 'BreadcrumbList', itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
            { '@type': 'ListItem', position: 2, name: cat.name, item: `${window.location.origin}/category/${cat.id}` },
          ]},
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: works.map((m, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: m.title,
              url: `${window.location.origin}/detail/${m.id}`,
            })),
          },
        }]
      : [],
  });

  if (!cat) return <Navigate to="/" replace />;

  return (
    <div className="bg-background-50">
      <Navbar />
      <main>
        <div className="relative overflow-hidden bg-foreground-950">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-background-300">
              <Link to="/" className="hover:text-accent-300 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span>题材书单</span>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-50">{cat.name}</span>
            </nav>
            <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="flex items-center gap-2 text-sm text-primary-300">
                  <i className={catIcons[cat.id] ?? 'ri-book-2-line'} /> {cat.tagline}
                </p>
                <h1 className="mt-2 font-heading text-3xl font-black text-background-50 md:text-5xl">
                  {cat.name}韩漫推荐
                </h1>
                <p className="mt-3 max-w-xl text-sm leading-7 text-background-300">{cat.desc}</p>
              </div>
              <div className="flex gap-4 text-background-100">
                <div>
                  <p className="font-heading text-2xl font-black text-accent-300">{works.length}</p>
                  <p className="text-xs text-background-300">收录作品</p>
                </div>
                <div className="border-l border-background-800 pl-4">
                  <p className="font-heading text-2xl font-black text-accent-300">{top?.score.toFixed(1) ?? '—'}</p>
                  <p className="text-xs text-background-300">最高评分</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-medium text-secondary-900">
              <i className="ri-price-tag-3-line" /> 全部 {cat.name}
            </span>
            {categories.filter((c) => c.id !== cat.id).map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="whitespace-nowrap rounded-full bg-background-100 px-3.5 py-1.5 text-xs text-foreground-600 ring-1 ring-background-200 transition-colors hover:bg-primary-500 hover:text-background-50 hover:ring-primary-500"
              >
                {c.name}
              </Link>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {works.map((m) => (
              <ManhwaCard key={m.id} item={m} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <button
        type="button"
        aria-label="返回顶部"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
      >
        <i className="ri-arrow-up-line text-lg" />
      </button>
    </div>
  );
}